import { cn } from "@/utils"

type Point = { label: string; detail: string }

interface AvailabilityBandProps {
  eyebrow: string
  heading: string
  subheading: string
  points: Point[]
  /** Accent colour for the top bar, eyebrow, and point labels. Defaults to brand blue. */
  accent?: string
  /** Applied to the outer card — use for max-width / margins per page context. */
  className?: string
}

/**
 * A reusable highlight band: eyebrow + heading + subheading + three proof points.
 * Static and hook-free so it can render inside server components (location pages).
 */
export function AvailabilityBand({
  eyebrow,
  heading,
  subheading,
  points,
  accent = "#004da9",
  className,
}: AvailabilityBandProps) {
  return (
    <div
      className={cn(
        "rounded-2xl bg-[#e8f1f8] border border-[#004da9]/15 shadow-xl overflow-hidden",
        className
      )}
    >
      <div className="h-3 w-full" style={{ backgroundColor: accent }} />
      <div className="p-8 md:p-6">
        <span
          className="inline-block text-[13px] font-bold tracking-[0.2em] uppercase mb-3"
          style={{ color: accent }}
        >
          {eyebrow}
        </span>
        <h2 className="text-[28px] md:text-[24px] font-bold text-[#002e65] leading-[1.2] mb-3">
          {heading}
        </h2>
        <p className="text-[16px] text-[#3a5a7c] leading-[1.7] mb-6 max-w-[640px]">
          {subheading}
        </p>
        <div className="grid grid-cols-3 md:grid-cols-1 gap-6 md:gap-4">
          {points.map((point) => (
            <div key={point.label}>
              <h3
                className="text-[16px] font-bold mb-1"
                style={{ color: accent }}
              >
                {point.label}
              </h3>
              <p className="text-[15px] text-[#3a5a7c] leading-[1.6]">
                {point.detail}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
